import { ARIA, CLASS_NAMES, EVENTS, SELECTORS, MISC } from '@bs/constants';

/**
 * A class for the Header module
 */
export default class Header {
  /**
   * Constructor function of the HeaderFunc class. Must receive one arguments, a DOM
   * node representing a delivery ticket.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    /**
     * DOM node that is passed into the constructor
     * @property {object} element DOM node that is passed into the constructor
     */
    this.element = element;
    this.animstate = false;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.search = this.element.querySelector(`${SELECTORS.SEARCH_FORM}`);
    this.searchbutton = this.element.querySelector(`${SELECTORS.FORM_CONTROL}`);
    this.searchinput = this.element.querySelector(`${SELECTORS.SEARCH_INPUT}`);
    this.hamburger = this.element.querySelector(`${SELECTORS.HEADER_CONTROL}`);
    this.burgericon = this.element.querySelector(
      `${SELECTORS.HEADER_CONTROL_BUTTON}`
    );
    this.mainnav = this.element.querySelector(`${SELECTORS.HEADER_NAV}`);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.searchbutton !== null) {
      this.searchbutton.addEventListener(EVENTS.CLICK, this.toggleSearchinput);
    }

    if (this.hamburger !== null) {
      this.hamburger.addEventListener(EVENTS.CLICK, this.toggleHamburger);
    }

    // Add document click listener to close mobile nav when clicking outside
    document.addEventListener(EVENTS.CLICK, this.handleOutsideClick);

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.toggleSearchinput = this.toggleSearchHandler.bind(this);
    this.toggleHamburger = this.toggleHamburgerHandler.bind(this);
    this.onKeyDown = this.onKeyDownHandler.bind(this);
    this.handleOutsideClick = this.handleOutsideClickHandler.bind(this);

    return this;
  }

  /**
   * This events trigger for toggling search input field on site header.
   * @param {object} e the click event of the search display button
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  toggleSearchHandler(e) {
    e.preventDefault();
    if (this.searchbutton.getAttribute(ARIA.EXPANDED) === MISC.FALSE) {
      this.searchbutton.setAttribute(ARIA.EXPANDED, MISC.TRUE);
      setTimeout(() => {
        this.searchinput.focus({ focusVisible: true });
      }, 0);
      document.addEventListener(EVENTS.KEY_DOWN, this.onKeyDown);
    } else {
      this.searchbutton.setAttribute(ARIA.EXPANDED, MISC.FALSE);
      document.removeEventListener(EVENTS.KEY_DOWN, this.onKeyDown);
    }

    this.element.classList.toggle(`${CLASS_NAMES.SHOW_SEARCH}`);

    return this;
  }

  /**
   * This events trigger for toggling hamburger menu.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  toggleHamburgerHandler() {
    if (this.hamburger.getAttribute(ARIA.EXPANDED) === MISC.FALSE) {
      this.hamburger.setAttribute(ARIA.EXPANDED, MISC.TRUE);
      document.addEventListener(EVENTS.KEY_DOWN, this.onKeyDown);
    } else {
      this.hamburger.setAttribute(ARIA.EXPANDED, MISC.FALSE);
      document.removeEventListener(EVENTS.KEY_DOWN, this.onKeyDown);
    }

    const anims = this.burgericon.querySelectorAll('animate');
    anims[+!!this.animstate].beginElement();
    this.animstate = !this.animstate;

    this.mainnav.classList.toggle(`${CLASS_NAMES.OPEN}`);

    return this;
  }

  /**
   * Handles clicks outside the mobile navigation to close it when open
   * @param {object} e the click event
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  handleOutsideClickHandler(e) {
    // Only process if mobile nav is open
    if (this.mainnav.classList.contains(CLASS_NAMES.OPEN)) {
      // Check if click is outside the nav and not on the hamburger button
      const isClickInsideNav = this.mainnav.contains(e.target);
      const isClickOnHamburger = this.hamburger.contains(e.target);

      if (!isClickInsideNav && !isClickOnHamburger) {
        this.toggleHamburgerHandler();
      }
    }

    return this;
  }

  /**
   * Execute on key down
   * @param {object} e the keydown event
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  onKeyDownHandler(e) {
    if (
      this.mainnav.classList.contains(CLASS_NAMES.OPEN) &&
      e.key === 'Escape'
    ) {
      this.toggleHamburgerHandler();
    }

    if (
      this.element.classList.contains(CLASS_NAMES.SHOW_SEARCH) &&
      e.key === 'Escape'
    ) {
      this.searchbutton.click();
    }

    if (
      this.element.classList.contains(CLASS_NAMES.SHOW_SEARCH) &&
      e.key === 'Enter'
    ) {
      this.search.submit();
    }

    return this;
  }
}
